import { taxonomyAPI } from '@/providers/apis/taxonomy/taxonomy';
import { AUDIENCE_TYPE, DEFAULT_LAUNCH_CONTAINER, GROUP_CODE_TYPE,
    LAUNCH_REQUEST_METHOD, LTI_VERSIONS,
    PUBLIC_KEY_TYPES, TOOL_CATEGORY, TOOL_TYPE } from '@/utils/constants';
import { validateUrl, sortByProperty } from '@/utils/utils';

export default class RegistryFormFields {

    /**
     * Help to form the config object based on tool type and public key type
     * @onTypeChange method help to switch tool type
     * @onPublicChange method help to switch public data
     */
    public get toolConfig() {
        const activeToolType = this.activeToolType;
        const activePublicKeyType = this.activePublicKeyType;
        const toolTypes: any = this.toolTypes;
        const publicKeyTypes: any = this.publicKeyTypes;
        const activeKey = { [activePublicKeyType]: publicKeyTypes[activePublicKeyType] } || {};
        const protocol = this.protocol;
        let toolConfig = Object.assign({}, protocol, toolTypes[activeToolType]);
        if (activeToolType === 'LTI') {
            toolConfig = Object.assign({}, toolConfig, activeKey);
        }
        return toolConfig;
    }

    public activeToolType: string = 'LTI';

    public activePublicKeyType: string = 'RSA';

    public toolDetails: any = {
        name: {
            name: 'Name',
            field: 'b-form-input',
            modelKey: 'name',
            modelValue: '',
            validate: { required: true },
        },
        thumbnail: {
            name: 'Logo',
            field: 'b-form-file',
            modelKey: 'thumbnail',
            isFileUpload: true,
            modelValue: [],
            placeholder: 'Choose file',
        },
        description: {
            name: 'Description',
            field: 'b-form-textarea',
            modelKey: 'description',
            modelValue: '',
            validate: { required: true },
        },
        partner: {
            name: 'Partner',
            field: 'b-form-select',
            modelKey: 'partner',
            modelValue: '',
            propBind: {
                'options': [],
                'text-field': 'partner_name',
                'value-field': 'partner_id',
            },
            validate: { required: true },
        },
    };


    public categoryInfo = {
        categories: {
            name: 'Discipline',
            field: 'b-form-select',
            modelKey: 'category',
            modelValue: '',
            propBind: {
                'options': [],
                'text-field': 'title',
                'value-field': 'id',
            },
            eventBind: { change: this.onChangeCategory.bind(this) },
        },
        subjects: {
            name: 'Subjects',
            field: 'b-form-select',
            modelKey: 'subject',
            modelValue: '',
            propBind: {
                'options': [],
                'text-field': 'title',
                'value-field': 'id',
            },
            eventBind: { change: this.onChangeSubject.bind(this) },
        },
        grades: {
            name: 'Grades',
            field: 'vue-multi-select',
            modelKey: 'grades',
            modelValue: '',
            propBind: {
                'options': [],
                'label': 'grade',
                'track-by': 'id',
                'multiple': true,
                'close-on-select': false,
                'placeholder': 'Please select grades',
            },
        },
    };

    public otherMetadata = {
        defaultLaunchContainer: {
            name: 'Default Launch Conainer',
            field: 'b-form-select',
            modelKey: 'defaultLaunchContainer',
            modelValue: 'Embed',
            propBind: {
                options: DEFAULT_LAUNCH_CONTAINER,
            },
        },
        toolCategory: {
            name: 'Tool Category',
            field: 'b-form-select',
            modelKey: 'toolCategory',
            modelValue: 'Curriculum',
            propBind: {
                options: TOOL_CATEGORY,
            },
        },
        audience: {
            name: 'Audience',
            field: 'b-form-select',
            modelKey: 'audience',
            modelValue: 'student',
            propBind: {
                options: AUDIENCE_TYPE,
            },
        },
        rating: {
            name: 'Rating',
            field: 'b-form-input',
            modelKey: 'rating',
            modelValue: '',
        },
        customParams: {
            name: 'Custom Parameters',
            field: 'b-form-textarea',
            modelKey: 'customParams',
            modelValue: '',
        },
    };


    public otherToolConfig = {
        requestMethod: {
            name: 'Launch Request Method',
            field: 'b-form-select',
            modelKey: 'requestMethod',
            modelValue: 'POST',
            propBind: {
                options: LAUNCH_REQUEST_METHOD,
            },
            validate: { required: true },
        },
        username: {
            name: 'Username',
            field: 'b-form-input',
            modelKey: 'username',
            modelValue: '',
            validate: { required: true },
        },
        clientCode: {
            name: 'Client Code',
            field: 'b-form-input',
            modelKey: 'clientCode',
            modelValue: '',
            validate: { required: true },
        },
        redirectURI: {
            name: 'Redirect URL',
            field: 'b-form-input',
            modelKey: 'redirectURI',
            modelValue: '',
            placeholder: 'Ex: http://example.com',
            validate: { url: {require_protocol: true } },
        },
        imageURLs: {
            name: 'Images',
            field: 'b-form-file',
            modelKey: 'imageURLs',
            modelValue: [],
        },
    };


    // Based on tool type it will render the config form
    public toolTypes = {
        LTI: {
            toolURL: {
                name: 'Tool URL',
                field: 'b-form-input',
                modelKey: 'toolURL',
                modelValue: '',
                placeholder: 'Ex: http://example.com',
                validate: { required: true, url: {require_protocol: true } },
            },
            clientId: {
                name: 'Client Id',
                field: 'b-form-input',
                modelKey: 'clientId',
                modelValue: '',
                validate: { required: true, min: 4 },
            },
            LTIVerison: {
                name: 'LTI Version',
                field: 'b-form-select',
                modelKey: 'LTIVersion',
                modelValue: '',
                propBind: {
                    options: LTI_VERSIONS,
                },
                validate: { required: true },
            },
            publicKeyType: {
                name: 'Public Key Type',
                field: 'b-form-select',
                modelKey: 'publicKeyType',
                modelValue: 'RSA',
                propBind: {
                    options: PUBLIC_KEY_TYPES,
                },
                eventBind: { change: this.onPublicChange.bind(this) },
                validate: { required: true },
            },
            initateLoginURL: {
                name: 'Initiate Login URL',
                field: 'b-form-input',
                modelKey: 'initateLoginURL',
                modelValue: '',
                placeholder: 'Ex: http://example.com',
                validate: { required: true , url: {require_protocol: true }},
            },
            redirectURI: {
                name: 'Redirect URL',
                field: 'b-form-input',
                modelKey: 'redirectURI',
                modelValue: '',
                placeholder: 'Ex: http://example.com',
                validate: { url: {require_protocol: true }},
            },
        },
        embed: Object.assign({}, this.otherToolConfig),
        script: Object.assign({}, this.otherToolConfig),
    };

    public contentRegistry = {
        contentURL: {
            name: 'Product Market URL',
            field: 'b-form-input',
            modelKey: 'contentURL',
            modelValue: '',
            validate: { required: true },
        },
        contentType: {
            name: 'Content Access',
            field: 'b-form-select',
            modelKey: 'contentType',
            modelValue: '',
            propBind: {
                options: GROUP_CODE_TYPE,
            },
            validate: { required: true },
        },
        audience: {
            name: 'Audience',
            field: 'b-form-select',
            modelKey: 'audience',
            modelValue: 'student',
            propBind: {
                options: AUDIENCE_TYPE,
            },
        },

    };

    private categoryList: any = [];

    private subjectList: any = [];

    private gradesList: any = [];

    private protocol = {
        toolType: {
            name: 'Protocol(Config will change based on this)',
            field: 'b-form-select',
            modelKey: 'toolType',
            modelValue: 'LTI',
            propBind: {
                options: TOOL_TYPE,
            },
            validate: { required: true },
            eventBind: { change: this.onTypeChange.bind(this) },
        },
    };


    private publicKeyTypes = {
        RSA: {
            name: 'Public Key',
            field: 'b-form-input',
            modelKey: 'publicKey',
            modelValue: '',
            validate: { required: true },
        },
        JWKS: {
            name: 'Public Set URL',
            field: 'b-form-input',
            modelKey: 'keysetURL',
            modelValue: '',
            validate: { required: true },
        },
    };

    constructor() {
        this.fetchCategoryList();
    }

    public selectedCategory(categoryId: any, subjectId: string) {
        const categoryList = this.categoryList;
        const subjectList = this.subjectList;
        const category = categoryList.find((item: any) => item.id === categoryId);
        const subject = subjectList.find((item: any) => item.id === subjectId);
        return {
            category,
            subject,
        };
    }


    // -----------------------------------------------------------------
    // Methods

    public fetchCategoryList() {
        taxonomyAPI.fetchTaxonomyClassifications().then((categoryList: any) => {
            this.categoryInfo.categories.propBind.options = categoryList;
            this.categoryList = categoryList;
        });
    }

    public getSubjectList(categoryId: string) {
        taxonomyAPI.fetchSubjects(categoryId).then((subjectList: any) => {
            this.categoryInfo.subjects.propBind.options = subjectList;
            this.subjectList = subjectList;
        });
    }

    public onChangeSubject(subjectId: any) {
        this.getGradesList(subjectId);
    }

    public onReset() {
        this.categoryInfo.grades.propBind.options = [];
        this.categoryInfo.subjects.propBind.options = [];
    }

    // -----------------------------------------------------------------
    // Actions

    private onPublicChange(publicType: string) {
        this.activePublicKeyType = publicType;
    }

    private onTypeChange(toolType: any) {
        this.activeToolType = toolType;
    }

    private onChangeCategory(categoryId: string) {
        this.getSubjectList(categoryId);
    }

    private getGradesList(subjectId: any) {
        taxonomyAPI.fetchTaxonomyGrades(subjectId, 'GUT').then((gradesList: any) => {
            sortByProperty(gradesList, 'sequence', 'ASC');
            this.categoryInfo.grades.propBind.options = gradesList;
            this.gradesList = gradesList;
        });
    }

    private onValidateURL(eventBind: any) {
        eventBind.target.value = validateUrl(eventBind.target.value);
    }
}
